/**
 * Язык интерфейса (D11): выбор человека, а не только язык браузера.
 *
 * До этого язык определялся при первом заходе по `navigator.language`
 * и менялся только в профиле. У незалогиненного профиля нет —
 * с английского браузера приложение открывалось по-английски,
 * и переключиться было некуда.
 *
 * Три источника, в этом порядке:
 *
 *   1. выбор человека в хранилище — он сильнее всего и переживает
 *      перезагрузку;
 *   2. язык профиля мастера, если он вошёл и своего выбора ещё
 *      не делал;
 *   3. язык браузера.
 *
 * Порядок именно такой: человек, нажавший «EN» на общем ноутбуке
 * за столом, должен получить английский и после перезагрузки —
 * даже если в его профиле стоит русский. Обратное («профиль сильнее»)
 * означало бы кнопку, которая молча отменяет саму себя.
 *
 * Значений цветов и строк здесь нет: это модуль про выбор, а сам
 * словарь живёт в `i18n.ts`.
 */

import { LANGUAGES, pickLanguage, type Language } from './i18n.js'

export const LANGUAGE_STORAGE_KEY = 'rollcall.language'

/** Хранилище выбора: то немногое от localStorage, что здесь нужно. */
export type LanguageStorage = Pick<Storage, 'getItem' | 'setItem'>

function isLanguage(value: string | null): value is Language {
  return value !== null && (LANGUAGES as readonly string[]).includes(value)
}

/** Выбор человека, если он был сделан; `null` — «не выбирал». */
export function readStoredLanguage(storage: LanguageStorage): Language | null {
  try {
    const stored = storage.getItem(LANGUAGE_STORAGE_KEY)
    return isLanguage(stored) ? stored : null
  } catch {
    // Приватный режим Safari бросает на любом обращении к хранилищу.
    // Язык — не повод не открыть лист персонажа.
    return null
  }
}

export function storeLanguage(storage: LanguageStorage, language: Language): void {
  try {
    storage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Выбор не переживёт перезагрузку — это всё, что здесь ломается.
  }
}

/**
 * С чего начинается сеанс: выбор человека, иначе язык браузера.
 *
 * Профиль сюда не приходит: на старте его ещё нет — сессия
 * восстанавливается позже, уже после первой отрисовки (D35).
 * Язык профиля подхватывается отдельно, когда профиль появился.
 */
export function initialLanguage(
  storage: LanguageStorage,
  browserLanguage: string | undefined,
): Language {
  return readStoredLanguage(storage) ?? pickLanguage(browserLanguage)
}

/** Языков два, поэтому переключатель, а не список. */
export function nextLanguage(language: Language): Language {
  return language === 'ru' ? 'en' : 'ru'
}
